import React, { type FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  Panel,
  Accordion,
  Text,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  SpacingVertical,
  Checkbox,
  HelpIcon,
  Flex,
  Select,
  TooltipUtils,
  FlexItem,
} from "mailup-web-components";
import { openHelp } from "Helpers/openHelp";
import { notifyParent } from "mailup-react-helpers";
import { useRecoilState, useRecoilValue } from "recoil";
import { importFileWizardState } from "Pages/ImportWizard/recoil/state/state";
import styled from "@emotion/styled";
import { messagesConfirmationState } from "Pages/ImportWizard/recoil/state/stateStep4";
import { useParams } from "react-router-dom";
import { copyPasteChannelTypeState } from "Pages/ImportWizard/recoil/state/stateCopyPaste";
import { gainsightTrack } from "mailup-react-helpers";

const AdvancedSettings: FC = () => {
  const intl = useIntl();
  const [
    {
      ReplaceGroups,
      OptOut,
      NotOverrideEmptyFields,
      SendConfirmationMail,
      ForceSubscription,
      Pending,
      ConfirmationEmailId,
    },
    setimportFileWizardState,
  ] = useRecoilState(importFileWizardState);
  const { type } = useParams();

  const { options } = useRecoilValue(messagesConfirmationState);

  const copyPasteChannelType = useRecoilValue(copyPasteChannelTypeState);

  useEffect(() => {
    TooltipUtils.rebuild();
  }, []);

  const openGuide = () => {
    gainsightTrack("mup_importWizard_clickOnlineHelpInAdvancedSettings", {});

    notifyParent("openHelp", [openHelp("importFileGuide")]);
  };

  const setReplaceGroups = () => {
    gainsightTrack("mup_importWizard_clickChooseRemoveRecipientsIfPresent", {});

    setimportFileWizardState((prev) => ({
      ...prev,
      ReplaceGroups: !prev.ReplaceGroups,
    }));
  };

  const setOptOut = () => {
    gainsightTrack("mup_importWizard_clickChooseImportUnsubscribe", {});

    setimportFileWizardState((prev) => ({
      ...prev,
      OptOut: !prev.OptOut,
    }));
  };

  const setNotOverrideEmptyFields = () => {
    gainsightTrack("mup_importWizard_clickChooseEmptyValueOverwritting", {});

    setimportFileWizardState((prev) => ({
      ...prev,
      NotOverrideEmptyFields: !prev.NotOverrideEmptyFields,
    }));
  };

  const setSendConfirmationMail = () => {
    gainsightTrack("mup_importWizard_clickChooseEmailConfirmation", {});

    setimportFileWizardState((prev) => ({
      ...prev,
      SendConfirmationMail: !prev.SendConfirmationMail,
    }));
  };

  const setForceSubscription = () => {
    gainsightTrack("mup_importWizard_clickChooseForceRegistration", {});

    setimportFileWizardState((prev) => ({
      ...prev,
      ForceSubscription: !prev.ForceSubscription,
    }));
  };

  const setPending = () => {
    setimportFileWizardState((prev) => ({
      ...prev,
      Pending: !prev.Pending,
    }));
  };

  const handleChangeConfirmationEmailId = (v: number) => {
    setimportFileWizardState((prev) => ({
      ...prev,
      ConfirmationEmailId: v,
    }));
  };

  const onExpandCollapse = () => {
    gainsightTrack("mup_importWizard_clickAdvancedImportSettings", {});

    TooltipUtils.rebuild();
  };

  return (
    <Panel>
      <Accordion
        label={intl.formatMessage({ id: "Load.FileImport.AdvancedSettings" })}
        onExpandCollapse={onExpandCollapse}
      >
        <SpacingVertical />

        <Text>
          {intl.formatMessage(
            { id: "Load.FileImport.AdvancedSettingsDescr" },
            {
              link: (chunks) => (
                <Link
                  variant={LINK_VARIANT_HIGHLIGHT}
                  onClick={() => openGuide()}
                >
                  {chunks}
                </Link>
              ),
            },
          )}
        </Text>

        <SpacingVertical />
        <Flex alignItems="flex-start" gap={8}>
          <Checkbox
            checked={ReplaceGroups}
            onChange={() => setReplaceGroups()}
            label={intl.formatMessage({
              id: "Load.FileImport.ReplaceGroups",
            })}
          />
          <StyledHelpIcon
            data-tip={intl.formatMessage({
              id: "Load.FileImport.ReplaceGroupsTooltip",
            })}
          />
        </Flex>
        <SpacingVertical />
        <Flex alignItems="flex-start" gap={8}>
          <Checkbox
            checked={OptOut}
            onChange={() => setOptOut()}
            label={intl.formatMessage({ id: "Load.FileImport.OptOut" })}
          />
          <StyledHelpIcon
            data-tip={intl.formatMessage({
              id: "Load.FileImport.OptOutTooltip",
            })}
          />
        </Flex>
        <SpacingVertical />
        <Flex alignItems="flex-start" gap={8}>
          <Checkbox
            checked={NotOverrideEmptyFields}
            onChange={() => setNotOverrideEmptyFields()}
            label={intl.formatMessage({
              id: "Load.FileImport.NotOverrideEmptyFields",
            })}
          />
          <StyledHelpIcon
            data-tip={intl.formatMessage({
              id: "Load.FileImport.NotOverrideEmptyFieldsTooltip",
            })}
          />
        </Flex>

        {type === "file" || copyPasteChannelType !== "MobileNumber" ? (
          <>
            <SpacingVertical variant="16" />
            <Flex gap={8} alignItems="center">
              <Checkbox
                checked={SendConfirmationMail}
                onChange={() => setSendConfirmationMail()}
                label={intl.formatMessage({
                  id: "Load.FileImport.SendConfirmationMail",
                })}
              />
              <StyledSelectContainer>
                <Select
                  disabled={!SendConfirmationMail}
                  value={ConfirmationEmailId}
                  onChange={handleChangeConfirmationEmailId}
                  options={options}
                  placeholder={intl.formatMessage({
                    id: "Load.FileImport.SelectMsgPlaceholder",
                  })}
                  searchable={false}
                />
              </StyledSelectContainer>
              <StyledHelpIcon
                data-tip={intl.formatMessage({
                  id: "Load.FileImport.SendConfirmationMailTooltip",
                })}
              />
            </Flex>
            <SpacingVertical variant="16" />
          </>
        ) : (
          <SpacingVertical />
        )}
        <Flex alignItems="flex-start" gap={8}>
          <Checkbox
            checked={ForceSubscription}
            onChange={() => setForceSubscription()}
            label={intl.formatMessage(
              {
                id: "Load.FileImport.ForceSubscription",
              },
              {
                strong: (chunks) => <strong>{chunks}</strong>,
              },
            )}
          />
          <StyledHelpIcon
            data-tip={intl.formatMessage({
              id: "Load.FileImport.ForceSubscriptionTooltip",
            })}
          />
        </Flex>

        {(type === "file" || copyPasteChannelType !== "MobileNumber") && (
          <>
            <SpacingVertical />
            <Flex alignItems="flex-start" gap={8}>
              <Checkbox
                checked={Pending}
                onChange={() => setPending()}
                label={intl.formatMessage(
                  { id: "Load.FileImport.Pending" },
                  {
                    strong: (chunks) => <strong>{chunks}</strong>,
                  },
                )}
              />
              <StyledHelpIcon
                data-tip={intl.formatMessage({
                  id: "Load.FileImport.PendingTooltip",
                })}
              />
            </Flex>
          </>
        )}
      </Accordion>
    </Panel>
  );
};
export default AdvancedSettings;

const StyledSelectContainer = styled(FlexItem)`
  min-width: 400px;
  flex-shrink: 0;
  margin: 0 8px
`;

const StyledHelpIcon = styled(HelpIcon)`
  position: relative;
  top: -2px;
`;
